import { PAYMENT_METHOD_LABELS, isCardPaymentMethod } from '../constants/payment-methods.js';
/** 65.57 — teto de parcelas no cartão de crédito (o mesmo da regra de taxa). */
export const MAX_CARD_INSTALLMENTS = 24;
/** 65.57 — textos das recusas de parcelas (critério 6), os mesmos em todo caminho do servidor. */
export const MAX_CARD_INSTALLMENTS_REFUSAL = `No máximo ${MAX_CARD_INSTALLMENTS} parcelas.`;
export const NON_CREDIT_INSTALLMENTS_REFUSAL = 'Parcelas valem só para cartão de crédito.';
/** 65.57 — sem regra cadastrada acima disso, o crédito oferece de 1x a 12x. */
export const DEFAULT_CARD_INSTALLMENTS = 12;
/**
 * 65.57 (decisão 1A): no crédito, as parcelas aparecem sempre — de 1x até 12x, ou até a maior
 * parcela com regra ativa quando ela passa de 12, sem passar do teto. A regra decide só a taxa.
 */
export function creditInstallmentOptions(ruleInstallments) {
    const largest = ruleInstallments.reduce((max, count) => (Number.isInteger(count) && count > max ? count : max), DEFAULT_CARD_INSTALLMENTS);
    const last = Math.min(largest, MAX_CARD_INSTALLMENTS);
    return Array.from({ length: last }, (_, index) => index + 1);
}
/**
 * 65.57: recusa do número de parcelas pedido para a forma de pagamento final. Parcelas acima
 * de 1 só no cartão de crédito, e nunca acima do teto. Devolve a mensagem, ou nulo quando vale.
 */
export function installmentsRefusal(paymentMethod, installments) {
    if (installments === null || installments === undefined)
        return null;
    if (!Number.isInteger(installments) || installments < 1)
        return 'Informe as parcelas como um número inteiro a partir de 1.';
    if (installments > MAX_CARD_INSTALLMENTS)
        return MAX_CARD_INSTALLMENTS_REFUSAL;
    if (installments > 1 && paymentMethod !== 'credit_card')
        return NON_CREDIT_INSTALLMENTS_REFUSAL;
    return null;
}
/** Percentual máximo de uma regra, em pontos-base (100%). */
export const FEE_PERCENT_BP_MAX = 10_000;
/** Maior valor em centavos que cabe num campo de dinheiro do banco (int4). */
export const FEE_MAX_CENTS = 2_147_483_647;
/**
 * Chave de comparação de um nome digitado (bandeira, máquina, provedor): Unicode NFKC, espaços
 * colapsados e sem sobra nas pontas, minúsculas. "Visa", " VISA " e "ｖｉｓａ" viram a mesma
 * chave. O texto de exibição é guardado à parte. Devolve vazio quando não sobra nada.
 */
export function canonicalFeeKey(text) {
    return text.normalize('NFKC').replace(/\s+/gu, ' ').trim().toLowerCase().normalize('NFKC');
}
/** Chave da bandeira, ou nulo quando ela não foi informada. */
export function cardBrandKeyOf(brand) {
    return canonicalFeeKey(brand ?? '') || null;
}
/** Texto de exibição de um nome digitado: sem sobra nas pontas e sem espaço dobrado. */
export function displayFeeName(text) {
    return text.normalize('NFC').replace(/\s+/gu, ' ').trim();
}
/** "Cartão de Crédito (PicPay)"; sem perfil, só "Cartão de Crédito". */
export function paymentMethodLabelWithProfile(method, profileName) {
    const label = PAYMENT_METHOD_LABELS[method];
    return profileName ? `${label} (${profileName})` : label;
}
export class FeeComputationError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FeeComputationError';
    }
}
const isCents = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= FEE_MAX_CENTS;
/**
 * Taxa de uma passagem simples: percentual sobre o que passou pelo meio, arredondado uma vez
 * (meio centavo sobe), mais o valor fixo uma vez. A conta é feita em inteiros largos:
 * `floor((base × pontos-base + 5000) / 10000) + fixo`. Entrada fora da faixa ou resultado que
 * não cabe no campo do banco é recusado, nunca truncado.
 */
export function simplePassFeeCents(input) {
    const { feeBaseCents, percentBp, fixedCents } = input;
    if (!isCents(feeBaseCents) || !isCents(fixedCents)
        || typeof percentBp !== 'number' || !Number.isInteger(percentBp) || percentBp < 0 || percentBp > FEE_PERCENT_BP_MAX) {
        throw new FeeComputationError('FEE_INPUT_INVALID', 'Base, percentual e valor fixo da taxa precisam ser inteiros dentro da faixa aceita.');
    }
    const fee = (BigInt(feeBaseCents) * BigInt(percentBp) + BigInt(5000)) / BigInt(10_000) + BigInt(fixedCents);
    if (fee > BigInt(FEE_MAX_CENTS)) {
        throw new FeeComputationError('FEE_OVERFLOW', 'A taxa calculada passa do maior valor aceito.');
    }
    return Number(fee);
}
/**
 * Base da taxa: o que de fato passou pelo meio externo. Em dinheiro é o entregue menos o
 * troco; nos demais meios é o valor inteiro passado, inclusive o que excedeu a cobrança.
 * Crédito da cliente não passa por meio externo: sem base e sem taxa.
 */
export function feeBaseCentsOf(leaf) {
    if (leaf.sourceType !== 'payment_method' || leaf.receivedAmount === null)
        return null;
    return leaf.receivedAmount - leaf.changeAmount;
}
// Parcelas existem no crédito (à vista = 1) e no boleto (as parcelas do plano; boleto avulso
// = 1). Bandeira só em cartão. Fora disso a dimensão não entra na comparação, mesmo que
// venha preenchida.
const HAS_INSTALLMENTS = new Set(['credit_card', 'boleto']);
function normalizeQuery(query) {
    return {
        paymentMethod: query.paymentMethod,
        profileId: query.paymentMethodProfileId ?? null,
        brandKey: isCardPaymentMethod(query.paymentMethod) ? cardBrandKeyOf(query.cardBrand) : null,
        installments: HAS_INSTALLMENTS.has(query.paymentMethod) ? query.installments ?? 1 : null,
    };
}
function levelOf(rule, query) {
    if (!rule.isActive || rule.paymentMethod !== query.paymentMethod)
        return null;
    const profileExact = rule.paymentMethodProfileId !== null;
    const brandExact = rule.cardBrandKey !== null;
    const installmentsExact = rule.installments !== null;
    if (profileExact && rule.paymentMethodProfileId !== query.profileId)
        return null;
    if (brandExact && rule.cardBrandKey !== query.brandKey)
        return null;
    if (installmentsExact && rule.installments !== query.installments)
        return null;
    return (1 + (profileExact ? 0 : 4) + (brandExact ? 0 : 2) + (installmentsExact ? 0 : 1));
}
/** 65.48: nenhuma porta grava regra de taxa para dinheiro. Código e aviso da recusa. */
export const FEE_RULE_CASH_HAS_NO_FEE = 'FEE_RULE_CASH_HAS_NO_FEE';
export const FEE_RULE_CASH_HAS_NO_FEE_MESSAGE = 'Pagamento em dinheiro não tem taxa.';
/**
 * Regra que vale para um pagamento. Entre as regras V2 ativas da forma, ganha a mais
 * específica na ordem perfil > bandeira > parcelas (oito níveis). Sem nenhuma, vale a regra
 * global de hoje da forma + parcelas. Sem nenhuma das duas, devolve nulo: a taxa fica a
 * conferir, nunca zero presumido.
 */
export function resolveFeeRule(query, rules) {
    // 65.48: dinheiro não tem taxa. Regra antiga que ainda exista para a forma não vale: o
    // pagamento nasce sem taxa, igual ao caso sem regra, e o caixa segue pelo valor cheio.
    if (query.paymentMethod === 'cash')
        return null;
    const normalized = normalizeQuery(query);
    let best = null;
    for (const rule of rules.v2) {
        const level = levelOf(rule, normalized);
        if (level !== null && (!best || level < best.level))
            best = { rule, level };
    }
    if (best) {
        const components = best.rule.calculationModel === 'event_components';
        return {
            source: 'v2',
            ruleId: best.rule.id,
            level: best.level,
            calculationModel: components ? 'event_components' : 'simple_pass',
            percentBp: components ? null : best.rule.percentBp,
            fixedCents: components ? null : best.rule.fixedCents,
        };
    }
    // Na regra global de hoje toda forma tem a linha de 1 parcela; parcelas só variam no crédito
    // (o boleto parcelado usa a linha única do boleto).
    const legacyInstallments = query.paymentMethod === 'credit_card' ? normalized.installments ?? 1 : 1;
    const legacy = rules.legacy.find((rule) => rule.isActive && rule.paymentMethod === query.paymentMethod && rule.installments === legacyInstallments);
    if (!legacy)
        return null;
    return {
        source: 'legacy',
        ruleId: legacy.id,
        level: 9,
        calculationModel: 'simple_pass',
        percentBp: legacy.feePercentBp,
        fixedCents: legacy.feeFixedCents,
    };
}
const sameRule = (a, b) => (a?.source ?? null) === (b?.source ?? null) && (a?.ruleId ?? null) === (b?.ruleId ?? null);
/**
 * Diz se a bandeira decide a taxa deste pagamento. Só decide quando existe regra de bandeira
 * que ganharia da regra aplicada sem bandeira, para o mesmo perfil e parcelamento.
 */
export function cardBrandChoiceFor(query, rules) {
    if (!isCardPaymentMethod(query.paymentMethod))
        return { decisive: false, brands: [] };
    const without = resolveFeeRule({ ...query, cardBrand: null }, rules);
    const labels = new Map();
    for (const rule of rules.v2) {
        if (rule.isActive && rule.paymentMethod === query.paymentMethod && rule.cardBrandKey && !labels.has(rule.cardBrandKey)) {
            labels.set(rule.cardBrandKey, rule.cardBrand ?? rule.cardBrandKey);
        }
    }
    const brands = [...labels.entries()]
        .filter(([key]) => !sameRule(resolveFeeRule({ ...query, cardBrand: key }, rules), without))
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    return { decisive: brands.length > 0, brands };
}
/** Taxa de um pagamento pela regra escolhida; nulo quando a regra não é de passagem simples. */
export function feeCentsByRule(rule, feeBaseCents) {
    if (rule.calculationModel !== 'simple_pass' || rule.percentBp === null || rule.fixedCents === null)
        return null;
    return simplePassFeeCents({ feeBaseCents, percentBp: rule.percentBp, fixedCents: rule.fixedCents });
}
export function validateFeeRevisionRequest(request) {
    const violations = [];
    if (!request.requestId || !request.expectedPartId) {
        violations.push({ code: 'FEE_REVISION_INVALID', message: 'Pedido de revisão de taxa incompleto.' });
    }
    if (typeof request.reason !== 'string' || request.reason.trim().length < 3) {
        violations.push({ code: 'FEE_REVISION_REASON_REQUIRED', message: 'Informe o motivo com ao menos 3 letras.' });
    }
    if (request.mode === 'confirm_zero') {
        if (request.feeAmount !== undefined && request.feeAmount !== 0) {
            violations.push({ code: 'FEE_REVISION_INVALID', message: 'Confirmar taxa zero não leva valor.' });
        }
    }
    else if (request.mode === 'actual') {
        if (!isCents(request.feeAmount)) {
            violations.push({ code: 'FEE_REVISION_INVALID', message: 'A taxa real precisa ser inteira em centavos, sem negativo.' });
        }
        else if (request.feeAmount === 0) {
            violations.push({ code: 'FEE_ZERO_REQUIRES_CONFIRMATION', message: 'Taxa zero precisa ser confirmada de propósito: use "Confirmar taxa zero".' });
        }
    }
    else {
        violations.push({ code: 'FEE_REVISION_INVALID', message: 'Tipo de revisão de taxa inválido.' });
    }
    return violations;
}
//# sourceMappingURL=payment-fee.js.map